import type { AnalysisState } from "./analysis.js";
import type { BrandView, LookView } from "./brand.js";

/**
 * The business profile: what the Studio knows about the company it is making things for (03 §business context).
 *
 * It is a **document**, not a settings form. The difference matters twice over: it is what actually gets sent to
 * the model in front of every brief, so it has to read like something a person wrote; and a page that arrives
 * written, to be checked, is the only version of this question anyone finishes — a nine-field form asked before any
 * value has been delivered is the wall [07](../../../docs/07-open-questions.md) A.5 warns about.
 *
 * So the owner gives one thing to look for, the lookup writes the page, and the owner checks it (ADR 0049): each
 * line says where it came from so a wrong guess is obviously a guess, and a line the lookup could only guess comes
 * with answers to tap.
 */

/** Where a section's text came from. Anything not `you` is a proposal until it is corrected. */
export type ProfileSource = "you" | "places" | "website" | "guessed";

/**
 * How far along the business is (03 §a business that is still an idea).
 *
 * Not a section of the page: *It's an idea* on the first screen sets it, and *What's changed?* can propose flipping it
 * ("we opened last week"). It changes what the page says at the top, which sections count, and what the lookup reads.
 * Unset means nobody has said, which reads as open — the commonest case.
 */
export type BusinessStage = "open" | "idea";

export interface ProfileSection {
  key: string;
  heading: string;
  body: string;
  source: ProfileSource;
}

/** The sections' keys, in the order the page shows them. */
export const PROFILE_KEYS = ["name", "what", "who", "loved", "rivals", "tone", "works", "where", "hours", "links"] as const;
export type ProfileKey = (typeof PROFILE_KEYS)[number];

export interface ProfileSectionSpec {
  key: ProfileKey;
  heading: string;
  /** What belongs in it, as a phrase. The model is told this when it proposes a change to the section. */
  about: string;
  /** False when a business that is still an idea has nothing to put here: there are no opening hours before a door. */
  forIdea: boolean;
}

/** The sections, in the order the page shows them. The first is the one a lookup starts from. */
export const PROFILE_SECTIONS: ReadonlyArray<ProfileSectionSpec> = [
  { key: "name", heading: "The business", about: "its name on the first line and its town on the second; for an idea with no name yet, what it is and where it will be", forIdea: true },
  { key: "what", heading: "What we sell", about: "what it sells, and what it is best known for", forIdea: true },
  { key: "who", heading: "Who buys it", about: "who its customers are, mostly", forIdea: true },
  { key: "loved", heading: "What customers say", about: "what its customers praise, and anything they complain about, as its reviews say it", forIdea: false },
  { key: "rivals", heading: "Who else they could choose", about: "the businesses nearby doing the same thing, and what makes this one different from them", forIdea: true },
  { key: "tone", heading: "How we sound", about: "how its posts and videos should sound", forIdea: true },
  { key: "works", heading: "What we want more of", about: "what it most wants to sell more of, which the Studio leans on when it has a choice", forIdea: true },
  { key: "where", heading: "Where to find us", about: "the address and anything people need to find it, or that it works from a van, from home or only online", forIdea: true },
  { key: "hours", heading: "When we are open", about: "the opening hours, one line per day or run of days", forIdea: false },
  { key: "links", heading: "Where we are online", about: "its website and its social handles, one per line", forIdea: true },
];

export interface BusinessProfile {
  sections: ProfileSection[];
  updatedAt: string | null;
  /** Absent until somebody says. See `BusinessStage`. */
  stage?: BusinessStage;
}

/**
 * One message of the Business conversation ADR 0027 kept on the row (`business_profiles.conversation`). Nothing writes
 * or shows it since ADR 0049; the column stays until a migration is next needed for something else.
 */
export interface BusinessMessage {
  role: "owner" | "guide";
  text: string;
  at: string;
  online?: boolean;
  setup?: boolean;
}

/**
 * What a business most often wants more of, offered as chips while the lookup reads (ADR 0049). Asked then because no
 * website says it, and generic because nothing is known about the business yet; the check screen offers answers
 * written for this business once the lookup has read it.
 */
export const WANT_MORE: readonly string[] = [
  "People through the door", "More regulars", "Bookings", "Orders online", "Bigger orders", "Customers from further away",
];

/** `GET /v1/workspace/profile` */
export interface ProfileResponse {
  available: boolean;
  profile: BusinessProfile;
  /** What reading the business is doing right now, if anything (`analysis.ts`). */
  analysis: AnalysisState;
  /** What the lookup gathered for the owner to choose from, while it waits for them (ADR 0029). Null otherwise. */
  brand: BrandView | null;
  /** The logo and colours every video is made with. */
  look: LookView;
  /**
   * Whether a Google listing is linked to this business — the id and nothing else, which is all Google's terms let
   * us keep ([ADR 0029](../../../docs/adr/0029-one-lookup-for-every-business-and-the-owner-chooses-what-to-keep.md)).
   *
   * A boolean rather than the listing, because that is the whole of what may be shown until a daily reading of the
   * rating is built and its terms are read properly ([14](../../../docs/14-open-items.md)). "Found you on Google" is
   * a fact the owner can act on; a rating we are not allowed to keep is not ours to put on a screen.
   */
  googleListing: boolean;
  /** The listing's own review form, from the place id and nothing else (37 §1). Null with no listing linked. */
  reviewLink: string | null;
  note?: string;
}

/** Google's review form for a place. One address for every customer: there is never a step in front of it (37 §1). */
export const reviewLink = (placeId: string): string =>
  `https://search.google.com/local/writereview?placeid=${encodeURIComponent(placeId)}`;

/** The message to copy, written by code and asking for no rating and offering nothing, which is Google's rule (37 §1). */
export const reviewMessage = (name: string, link: string): string =>
  `Thank you for choosing ${name.trim() || "us"}. If you have a minute, would you leave a review on Google? It helps other people find us, and honest words are all we ask for.\n\n${link}`;

/** `POST /v1/workspace/look/logo`: the profile, and the colours the uploaded logo is drawn in. */
export interface LogoUploadResponse extends ProfileResponse {
  logoColours: string[];
}

/**
 * The profile as the database holds it: one row per workspace, the answers in `facts` and where each came from in
 * `sources` (`business_profiles`). Both columns existed before this screen did, which is why nothing was migrated.
 *
 * Anything with an empty body is dropped rather than carried as a blank section: a lookup that found nothing and a
 * question nobody asked are the same thing to everyone downstream.
 */
export function profileFromStored(
  facts: Record<string, unknown>,
  sources: Record<string, string>,
  updatedAt: Date | string | null,
): BusinessProfile {
  const sections: ProfileSection[] = [];
  for (const spec of PROFILE_SECTIONS) {
    const body = typeof facts[spec.key] === "string" ? (facts[spec.key] as string) : "";
    if (!body.trim()) continue;
    const source = sources[spec.key];
    sections.push({
      key: spec.key,
      heading: spec.heading,
      body,
      source: source === "places" || source === "website" || source === "guessed" ? source : "you",
    });
  }
  const said = facts.stage;
  const stage: BusinessStage | undefined = said === "idea" || said === "open" ? said : undefined;
  return { sections, updatedAt: updatedAt instanceof Date ? updatedAt.toISOString() : updatedAt, ...(stage ? { stage } : {}) };
}

/** The `name` column before anybody has said one. It is a placeholder, not an answer, and never drawn as one. */
export const UNNAMED = "Your business";

/**
 * The profile out of its row. The `name` column is older than `facts` and still answers for a row written before the
 * page existed — unless it is only the placeholder, which a row made by the first thing somebody said carries until
 * they say a name.
 */
export function profileOfRow(row: { name: string; facts: Record<string, unknown>; sources: Record<string, string>; updatedAt: Date | string | null }): BusinessProfile {
  const named = row.name && row.name !== UNNAMED ? { name: row.name } : {};
  return profileFromStored({ ...named, ...row.facts }, row.sources, row.updatedAt);
}

/**
 * The name and the town, out of "The business" as it is written.
 *
 * The page writes them on two lines; a person typing in one box writes "Bramley Bakehouse, Ledbury". Both mean the
 * same, and the map lookup and the `name` column need them apart.
 */
export function nameAndTown(body: string): { name: string; town: string } {
  const text = body.trim();
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  if (lines.length > 1) return { name: lines[0]!, town: (lines[1]!.split(",")[0] ?? "").trim() };
  const [name = "", town = ""] = text.split(",");
  return { name: name.trim(), town: town.trim() };
}

const filled = (p: BusinessProfile, key: string) => p.sections.find((s) => s.key === key && s.body.trim().length > 0);

/** The sections this business has something to put in, in order. An idea is not counted short for opening hours. */
export function profileAsked(p: BusinessProfile): ProfileSectionSpec[] {
  return PROFILE_SECTIONS.filter((s) => p.stage !== "idea" || s.forIdea);
}

/** The sections still empty, in page order. */
export function profileMissing(p: BusinessProfile): string[] {
  return profileAsked(p).filter((s) => !filled(p, s.key)).map((s) => s.key);
}

/**
 * The five the Studio actually writes from, and the three it does not.
 *
 * A brief is written for a business: what it sells, who buys it, how it sounds, what it wants more of, and the name
 * on the front. Where it is, when it opens and where it is online are what a lookup fills in and what goes on
 * screen at the end of a video — useful, but a post written without them is still a post written for this business,
 * and a plumber who never claimed a listing should not be told the Studio is not ready for him.
 *
 * So this is the line the Studio warns on and the line the app lands on the Overview for. It is deliberately shorter than `PROFILE_SECTIONS`: "complete" is a number the tab shows, "enough
 * to write from" is this.
 */
export const PROFILE_ESSENTIAL: readonly string[] = ["name", "what", "who", "tone", "works"];

/** The essential sections still empty, in page order. Empty means the Studio knows enough. */
export function profileGaps(p: BusinessProfile): Array<{ key: string; heading: string }> {
  return PROFILE_SECTIONS.filter((s) => PROFILE_ESSENTIAL.includes(s.key) && !filled(p, s.key))
    .map((s) => ({ key: s.key, heading: s.heading }));
}

/** Whether the Studio has enough to write a brief from. */
export function profileReady(p: BusinessProfile): boolean {
  return profileGaps(p).length === 0;
}

/** How complete it is, as a fraction, for the one number the tab shows. */
export function profileProgress(p: BusinessProfile): number {
  const asked = profileAsked(p).length;
  return (asked - profileMissing(p).length) / asked;
}

/**
 * The document itself — the string that goes to the model, and the string the customer can read and take away.
 *
 * Built here rather than in the browser because the screen showing it and the worker sending it have to be looking
 * at the same words. A section nobody has answered is left out entirely: a heading followed by nothing tells the
 * model less than silence does, and reads as a gap in the business rather than a gap in the profile.
 */
export function profileMarkdown(p: BusinessProfile): string {
  const name = filled(p, "name")?.body.split("\n")[0]?.trim();
  const lines: string[] = [`# ${name || "The business"}`, ""];
  // Said once, at the top, because it changes every post after it: before opening, the job is an audience.
  if (p.stage === "idea") lines.push("Not open yet: this business is still an idea.", "");
  for (const spec of PROFILE_SECTIONS) {
    if (spec.key === "name") continue;
    const section = filled(p, spec.key);
    if (!section) continue;
    lines.push(`## ${spec.heading}`, "", section.body.trim(), "");
  }
  return lines.join("\n").trimEnd() + "\n";
}

/**
 * Somewhere to be online, for a business that is not yet (03 §where to be online).
 *
 * Everything the Studio makes needs somewhere to go, and a business with no website and no page is where people
 * check it is real and find nothing. So the check screen of a business the lookup found nowhere says it does not
 * need a website to start, and shows these when asked. The list is ours, so what it recommends is something a person
 * chose and can label. Advice, not integration: plain links, nothing sent.
 *
 * `sponsored` is for later. A place in this list may be paid for one day, and the day it is, the screen labels it
 * *Sponsored* and the link says so. A paid recommendation nobody can tell from a free one is an advert pretending not
 * to be (CAP Code rule 2.1 in the UK), and the trust in the rest of the list is the thing a sponsor would be buying.
 */
export interface OnlineTool {
  kind: "social" | "listing" | "site";
  name: string;
  url: string;
  /** What it is for, in the words after the name. Lower case, no full stop. */
  what: string;
  sponsored: boolean;
}

/** The groups, in the order they are shown: the free one first, because it is enough to start. */
export const ONLINE_KINDS: Record<OnlineTool["kind"], string> = {
  social: "A social page — start here",
  listing: "On the map",
  site: "A website, when you want one",
};

export const ONLINE_ADVICE =
  "Worth doing early: a website, or even one social page. It is where people check you are real, where your posts send them, and how a customer finds you again. A free social page is enough to start.";

export const ONLINE_TOOLS: readonly OnlineTool[] = [
  { kind: "social", name: "Instagram", url: "https://www.instagram.com/", what: "free; where short videos of a local business get seen", sponsored: false },
  { kind: "social", name: "TikTok", url: "https://www.tiktok.com/", what: "free; reaches people who do not follow you yet", sponsored: false },
  { kind: "social", name: "Facebook page", url: "https://www.facebook.com/pages/create", what: "free; still where many locals check hours and reviews", sponsored: false },
  { kind: "listing", name: "Google Business Profile", url: "https://www.google.com/business/", what: "free; puts you on Google Maps once you open, shop or no shop", sponsored: false },
  { kind: "site", name: "Wix", url: "https://www.wix.com/", what: "a proper website without code, free to start", sponsored: false },
  { kind: "site", name: "Shopify", url: "https://www.shopify.com/", what: "if you will sell things online", sponsored: false },
];
